import React, { useEffect, useState } from 'react';
import { CloudSlash, ArrowsClockwise } from '@phosphor-icons/react';

/**
 * Tiny header indicator that surfaces two facts to the user:
 *   - Are we currently offline? (red WiFi-slash icon + "Offline" label)
 *   - Are there any mutations waiting to sync? (pulsing cyan dot + count)
 *
 * Listens for the 'defectr:queue:change' window event fired by the
 * offline cache whenever an item is enqueued or drained, so the badge
 * count stays live without polling.
 */
export default function OfflineIndicator() {
    const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
    const [queueCount, setQueueCount] = useState(0);

    useEffect(() => {
        const onUp = () => setOnline(true);
        const onDown = () => setOnline(false);
        const onQueue = (e) => setQueueCount(e?.detail?.count ?? 0);
        window.addEventListener('online', onUp);
        window.addEventListener('offline', onDown);
        window.addEventListener('defectr:queue:change', onQueue);
        return () => {
            window.removeEventListener('online', onUp);
            window.removeEventListener('offline', onDown);
            window.removeEventListener('defectr:queue:change', onQueue);
        };
    }, []);

    if (online && queueCount === 0) return null;

    if (!online) {
        return (
            <div
                className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#FEE2E2] border border-[#E53935] text-[#991B1B] font-mono text-[10px] uppercase tracking-wider"
                title="You're offline — changes will sync when you're back online"
                data-testid="offline-indicator"
            >
                <CloudSlash size={14} weight="bold" />
                <span className="hidden sm:inline">Offline</span>
                {queueCount > 0 && <span className="tabular-nums">· {queueCount}</span>}
            </div>
        );
    }

    // Online but the drain hasn't fully completed — show pending badge
    return (
        <div
            className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#E8F5FB] border border-[#1FA5CF] text-[#1787AB] font-mono text-[10px] uppercase tracking-wider"
            title={`${queueCount} change${queueCount === 1 ? '' : 's'} syncing…`}
            data-testid="syncing-indicator"
        >
            <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
            <span className="tabular-nums">{queueCount}</span>
            <span className="hidden sm:inline">Syncing</span>
        </div>
    );
}
